import { Link } from "react-router-dom";
import {
  User2,
  Beef,
  Home,
  Building2,
  Tractor,
  CreditCard,
  PiggyBank,
  Users2,
  ArrowRight,
  Info,
  Lightbulb,
  ShieldCheck,
  FileCheck2,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";

const LOAN_RATES = [
  {
    icon: User2,
    name: "Personal Loan",
    nameGu: "પર્સનલ લોન",
    rate: "15% onwards",
    min: "₹10,000",
    max: "₹2,00,00,000",
    tenure: "36 months",
  },
  {
    icon: Beef,
    name: "Cattle Loan (Pashupalan)",
    nameGu: "કેટલ લોન (પશુપાલન લોન)",
    rate: "15% onwards",
    min: "₹15,000",
    max: "₹2,00,00,000",
    tenure: "24 months",
  },
  {
    icon: Home,
    name: "Mortgage / Home Loan",
    nameGu: "મોરટગેજ / હોમ લોન",
    rate: "15% onwards",
    min: "₹10,000",
    max: "₹2,00,00,000",
    tenure: "48 months",
  },
  {
    icon: Building2,
    name: "Housing Loan",
    nameGu: "હાઉસિંગ લોન",
    rate: "15% onwards",
    min: "₹1,00,000",
    max: "₹10,00,00,000",
    tenure: "60 months",
  },
  {
    icon: Tractor,
    name: "Agriculture Loan",
    nameGu: "એગ્રીકલ્ચર લોન",
    rate: "15% onwards",
    min: "₹10,000",
    max: "₹2,00,00,000",
    tenure: "48 months",
  },
  {
    icon: CreditCard,
    name: "Loan Against Credit Card",
    nameGu: "લોન અગેઇન્સ્ટ ક્રેડિટ કાર્ડ",
    rate: "15% onwards",
    min: "₹10,000",
    max: "₹5,00,000",
    tenure: "24 months",
  },
  {
    icon: PiggyBank,
    name: "Loan Against FD",
    nameGu: "લોન અગેઇન્સ્ટ FD",
    rate: "6.50% onwards",
    min: "₹10,000",
    max: "As per FD value",
    tenure: "36 months",
  },
  {
    icon: Users2,
    name: "Loan Against Social Credit",
    nameGu: "લોન અગેઇન્સ્ટ સોશિયલ ક્રેડિટ",
    rate: "Customised rate",
    min: "₹10,000",
    max: "₹2,00,00,000",
    tenure: "48 months",
  },
  {
    icon: Home,
    name: "Plot Loan",
    nameGu: "પ્લોટ લોન",
    rate: "15% onwards",
    min: "₹1,00,000",
    max: "₹5,00,00,000",
    tenure: "60 months",
  },
  {
    icon: Home,
    name: "Loan Against Open Plot",
    nameGu: "લોન અગેઇન્સ્ટ ઓપન પ્લોટ",
    rate: "15% onwards",
    min: "₹1,00,000",
    max: "₹5,00,00,000",
    tenure: "48 months",
  },
  {
    icon: PiggyBank,
    name: "Gold Loan",
    nameGu: "ગોલ્ડ લોન",
    rate: "15% onwards",
    min: "₹10,000",
    max: "₹1,00,00,000",
    tenure: "24 months",
  },
  {
    icon: PiggyBank,
    name: "Gold Loan for Buy",
    nameGu: "ગોલ્ડ લોન (ખરીદી માટે)",
    rate: "15% onwards",
    min: "₹25,000",
    max: "₹1,00,00,000",
    tenure: "36 months",
  },
  {
    icon: ShieldCheck,
    name: "Loan Against Security Deposit",
    nameGu: "લોન અગેઇન્સ્ટ સિક્યુરિટી ડિપોઝિટ",
    rate: "8% onwards",
    min: "₹25,000",
    max: "₹2,00,00,000",
    tenure: "48 months",
  },
];

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "No CIBIL Score Required" },
  { icon: User2, label: "No Fixed Income Required" },
  { icon: FileCheck2, label: "Quick & Simple Process" },
  { icon: Lightbulb, label: "Best Loan Solution per your need" },
];

const IMPORTANT_NOTES = [
  "Displayed rates are starting (indicative) rates. Final rate may vary as per application, profile and eligibility.",
  "Processing fee, documentation charge, late payment charge and other applicable terms may apply separately.",
  "Loan approval is subject to the company's internal policy and eligibility criteria.",
  "Before availing a loan, please review all terms, conditions, interest rate and charges in full.",
];

const ADVICE = [
  "Always confirm the interest rate, processing fee, total payable amount and all terms in writing before signing a loan.",
];

export default function LoanRates() {
  return (
    <div
      data-testid="loan-rates-page"
      className="min-h-screen bg-[#04102B] text-white relative overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/8 blur-3xl" />

      <PolicyHeader testId="rates-header" />

      <main id="main-content" tabIndex={-1} className="relative max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20 space-y-16">
        {/* HERO */}
        <section data-testid="rates-hero" className="text-center">
          <span className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
            · Smart Finance Solutions ·
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight">
            Smart Finance Solutions
            <br />
            <span className="text-[#D4AF37]">For Every Need.</span>
          </h1>
          <p className="mt-5 font-display text-xl text-[#D4AF37]">
            સહયોગ આપણો... વિકાસ આપણો...
          </p>
        </section>

        {/* DESKTOP RATE TABLE */}
        <section data-testid="rates-table-wrap" className="hidden md:block">
          <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/30">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Loan Type · લોન પ્રકાર</th>
                  <th className="px-4 py-3 text-left">Interest (Annual) · વ્યાજદર</th>
                  <th className="px-4 py-3 text-left">Min. Loan · લઘુતમ</th>
                  <th className="px-4 py-3 text-left">Max. Loan · મહત્તમ</th>
                  <th className="px-4 py-3 text-left">EMI · હપ્તા (Months)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {LOAN_RATES.map((row, i) => (
                  <tr
                    key={row.name}
                    data-testid={`rates-row-${i}`}
                    className={i % 2 === 0 ? "bg-white/[0.02]" : "bg-transparent"}
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <row.icon className="text-[#F0D788] mt-0.5" size={20} />
                        <div>
                          <div className="font-medium">{row.name}</div>
                          <div className="text-xs text-white/55 font-display">{row.nameGu}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span className="font-display text-lg text-[#D4AF37]">{row.rate}</span>
                    </td>
                    <td className="px-4 py-4 align-top whitespace-nowrap">{row.min}</td>
                    <td className="px-4 py-4 align-top whitespace-nowrap">{row.max}</td>
                    <td className="px-4 py-4 align-top whitespace-nowrap">{row.tenure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* MOBILE CARDS */}
        <section data-testid="rates-cards" className="md:hidden space-y-3">
          {LOAN_RATES.map((row, i) => (
            <div
              key={row.name}
              data-testid={`rates-card-${i}`}
              className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] p-5"
            >
              <div className="flex items-start gap-3">
                <row.icon className="text-[#F0D788]" size={22} />
                <div className="flex-1">
                  <div className="font-medium">{row.name}</div>
                  <div className="text-xs text-white/55 font-display">{row.nameGu}</div>
                </div>
                <span className="font-display text-xl text-[#D4AF37]">{row.rate}</span>
              </div>
              <div className="mt-3 grid grid-cols-1 min-[400px]:grid-cols-3 gap-2 text-xs">
                <Cell k="Min" v={row.min} />
                <Cell k="Max" v={row.max} />
                <Cell k="EMI" v={row.tenure} />
              </div>
            </div>
          ))}
        </section>

        {/* TRUST STRIP */}
        <section
          data-testid="rates-trust"
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {TRUST_STRIP.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 text-center"
            >
              <Icon className="text-emerald-300 mx-auto" size={22} />
              <div className="mt-2 text-[11px] tracking-[0.16em] uppercase text-white/85">
                {label}
              </div>
            </div>
          ))}
        </section>

        {/* NOTES + ADVICE */}
        <section className="grid lg:grid-cols-2 gap-6">
          <div
            data-testid="rates-notes"
            className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-2 text-[#D4AF37] text-[11px] tracking-[0.22em] uppercase">
              <Info size={16} /> Important Notes · મહત્વપૂર્ણ સૂચનાઓ
            </div>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              {IMPORTANT_NOTES.map((n, i) => (
                <li key={`note-${i}-${String(n).slice(0, 32)}`} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            data-testid="rates-advice"
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6"
          >
            <div className="flex items-center gap-2 text-emerald-300 text-[11px] tracking-[0.22em] uppercase">
              <Lightbulb size={16} /> Advice · સલાહ
            </div>
            <ul className="mt-4 space-y-3 text-sm text-white/85">
              {ADVICE.map((a, i) => (
                <li key={`adv-${i}-${String(a).slice(0, 32)}`} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-emerald-300 shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CONTACT */}
        <section
          data-testid="rates-contact"
          className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-8 md:p-10"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
                · Contact · સંપર્ક ·
              </div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl">
                Talk to an advisor about your loan eligibility.
              </h3>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="tel:+917016975324"
                  data-testid="rates-call-1"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Phone size={14} /> 70169 75324
                </a>
                <a
                  href="tel:+919879630926"
                  data-testid="rates-call-2"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Phone size={14} /> 98796 30926
                </a>
                <a
                  href="https://wa.me/917016975324"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="rates-whatsapp"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-600/30"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href="mailto:sahyogpfir@gmail.com"
                  data-testid="rates-email"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Mail size={14} /> sahyogpfir@gmail.com
                </a>
              </div>
              <div className="mt-6 flex items-start gap-2 text-sm text-white/70">
                <MapPin size={16} className="text-[#D4AF37] mt-0.5" />
                <span>
                  Registered Office: A-106, Ambaji Complex, Unai Road, Vyara, Tapi, Gujarat – 394650
                </span>
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-[#D4AF37]/20 p-6 text-center">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
                · Website · વેબસાઈટ ·
              </div>
              <div className="mt-2 font-display text-xl">sahyogtapi.in</div>
              <Link
                data-testid="rates-apply-cta"
                to="/apply/loan"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold"
              >
                Apply for a Loan <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* Loans by location — internal links to local landing pages */}
        <section className="mt-16 max-w-6xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
            Loans by Location
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">
            Dedicated pages for {`Vyara, Tapi & Surat`}
          </h2>
          <p className="text-sm text-white/65 max-w-2xl mb-6">
            Looking for the same product in your city? Each page shows local eligibility,
            documents and a direct call-back option from our nearest team.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              ["personal-loan-vyara", "Personal Loan in Vyara"],
              ["personal-loan-tapi", "Personal Loan in Tapi"],
              ["personal-loan-surat", "Personal Loan in Surat"],
              ["business-loan-vyara", "Business Loan in Vyara"],
              ["business-loan-tapi", "Business Loan in Tapi"],
              ["business-loan-surat", "Business Loan in Surat"],
              ["agriculture-loan-vyara", "Agriculture Loan in Vyara"],
              ["agriculture-loan-tapi", "Agriculture Loan in Tapi"],
              ["agriculture-loan-surat", "Agriculture Loan in Surat"],
              ["housing-loan-vyara", "Housing Loan in Vyara"],
              ["housing-loan-tapi", "Housing Loan in Tapi"],
              ["housing-loan-surat", "Housing Loan in Surat"],
            ].map(([slug, label]) => (
              <Link
                key={slug}
                to={`/loan/${slug}`}
                data-testid={`rates-local-link-${slug}`}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 transition group"
              >
                <span className="text-sm text-white/80 group-hover:text-white">{label}</span>
                <ArrowRight size={14} className="text-[#F0D788]" />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function Cell({ k, v }) {
  return (
    <div className="rounded-lg bg-[#0A1F44] px-3 py-2 min-w-0">
      <div className="text-[9px] tracking-[0.2em] uppercase text-white/55">{k}</div>
      <div className="mt-0.5 text-white wrap-anywhere">{v}</div>
    </div>
  );
}
